import { useMemo } from "react";

import _ from 'lodash';
import {
  setISOWeek,
  getMonth,
  getISOWeek,
  format,
  startOfWeek,
  lastDayOfWeek,
  getDaysInMonth,
} from "date-fns";
import { MONTHS } from "./statuses";

export const lightBlue = "#4086A4";
export const MetricPeriodStatuses = {
  NORMAL: {
    value: "NORMAL",
    color: "#fff",
    tickLabelColor: "#005e85"
  },
  TRIGGER: {
    value: "TRIGGER",
    color: "#dc3545",
    tickLabelColor: "#dc3545",
  },
  NON_WORKING: {
    value: "NON_WORKING",
    color: "#212529",
    tickLabelColor: "#fff",
  },
};

export const MetricStatuses = {
  RED: {
    value: "RED",
    color: "#dc3545",
  },
  BLUE: {
    value: "BLUE",
    color: localStorage.getItem("colour") === "Green" ? '#28a745' : "#005e85"
  },
};

export const toNextPeriodStatus = (metric) => {
  const { NORMAL, TRIGGER, NON_WORKING } = MetricPeriodStatuses;

  switch (metric.periodStatus) {
    case NORMAL.value:
      return TRIGGER.value;

    case TRIGGER.value:
      return NON_WORKING.value;

    case NON_WORKING.value:
      return NORMAL.value;

    default:
      // Incase of unset status (for a new metric),
      // we toggle to TRIGGER as if NORMAL was set
      return TRIGGER.value;
  }
};

export const useDynamicDomain = (data, domain, target, lowerTrigger, upperTrigger) => {

  return useMemo(() => {
    const minData = _.minBy(data, (d) => {
      return d.y
    })
    const maxData = _.maxBy(data, (d) => {
      return d.y
    })

    let all = []

    if (minData) {
      all.push(minData.y)
    }

    if (maxData) {
      all.push(maxData.y)
    }

    if (target && target.length > 0) {
      all.push(...target)
    }

    if (lowerTrigger && lowerTrigger.length > 0) {
      all.push(...lowerTrigger)
    }

    if (upperTrigger && upperTrigger.length > 0) {
      all.push(...upperTrigger)
    }


    all = all.filter(val => val !== "")

    if (all.length < 1 || (all[0] === 0 && all[0] === 0 && all.length < 3)) {
      all = domain
    }
    const min = _.minBy(all, (d) => {
      return d
    })
    const max = _.maxBy(all, (d) => {
      return d
    })

    return [min, max]

  }, [data, domain, target, lowerTrigger, upperTrigger]);
};

export const useDateString = (data) =>
  useMemo(() => {
    const date = setISOWeek(new Date(), data.x);
    const first = format(startOfWeek(date), "d");
    const last = format(lastDayOfWeek(date), "d");
    const month = format(date, "MMM");
    const year = date.getFullYear();
    return `(${first}-${last} ${month} ${year})`;
  }, [data.x]);

// For a given date, returns an array of ISO week numbers (int)
export const getISOWeeksInMonth = (date) => {
  let dates = [];
  // Get all thursdays within the current month
  const thursdays = getThursdays(date)

  thursdays.map(day => dates.push(getISOWeek(day)))
  return dates;
};

export const getThursdays = (date) => {
  var d = new Date(date),
    month = d.getMonth(),
    thursdays = [];

  d.setDate(1);

  // Get the first Monday in the month
  while (d.getDay() !== 4) {
    d.setDate(d.getDate() + 1);
  }

  // Get all the other Mondays in the month
  while (d.getMonth() === month) {
    thursdays.push(new Date(d.getTime()));
    d.setDate(d.getDate() + 7);
  }

  return thursdays;
}

// Transforms the weekly metric data to an array of graph coordinates
export const useMetricData = (goal, date, isTrend, monthValues) => {

  // switch to dialy which is 1- 31 for the month
  // or switch to yearly
  const graphData = useMemo(() => {
    let dates = [];
    if (isTrend) {
      dates = monthValues
    } else {
      switch (goal.frequency) {
        case "Daily":
          dates = getDaysInMonthArray(date);
          break;
        case "Weekly":
          dates = getISOWeeksInMonth(date);
          break;
        default:
          dates = monthValues
      }
    }

    const metrics = goal.metrics.reduce((all, metric) => {
      return {
        ...all,
        [metric.periodValue]: {
          value: metric.value,
          id: metric.id,
          status: metric.status,
          periodStatus: metric.periodStatus,
          target: metric.target
        },
      };
    }, {});
    return dates.map((day) => {

      const m = metrics[day] || {};
      return {
        x: dates.indexOf(day) + 1,
        y: m.value === undefined ? null : m.value,
        id: m.id || null,
        status: m.status || MetricStatuses.BLUE.value,
        periodStatus: m.periodStatus || MetricPeriodStatuses.NORMAL.value,
        target: m.target === undefined ? null : m.target,
        date: date
      };
    });
  }, [isTrend, goal, date, monthValues]);
  return graphData;
};

export const getPeriodDisplayText = (frequency) => {
  switch (frequency) {
    case "Daily":
      return "Day"
    case "Weekly":
      return "Week"
    default:
      return "Month"
  }
}

export const getPeriodKey = (fiscalYearStartMonth, date, frequency) => {
  const currentMonth = date.getMonth() + 1

  if (frequency === "Daily") {
    return `${date.getMonth() + 1}${date.getFullYear()}`
  }

  if (frequency === "Weekly") {
    return `${date.getFullYear()}`
  }

  return currentMonth < fiscalYearStartMonth ? `${(date.getFullYear() - 1)}` : `${date.getFullYear()}`
}

export function evaluateGoalId(periodKey, fyStartMonth, periodValues, currentStartWeek) {
  const currentDate = new Date();

  let currMonth = currentDate.getMonth() + 1;
  let currYear = currentDate.getFullYear();

  const currFYStart = currMonth >= fyStartMonth ? currYear : currYear - 1;

  if (typeof periodKey !== "string") {
    const fyStartYear = parseInt(periodKey[0]);

    if (fyStartYear === currFYStart) return "current";
    if (fyStartYear < currFYStart) return "past";
    return "future";
  } else if (String(periodKey).length === 4) {
    if(currentStartWeek && periodValues[0] < currentStartWeek.week) {
      return "past";
    }
    return "current";
  } else {
    let str = String(periodKey);
    let month, year;
    if (str.length === 5) {
      month = Number(str[0]);
      year = Number(str.slice(1));
    } else if (str.length === 6) {
      month = Number(str.slice(0, 2));
      year = Number(str.slice(2));
    } 
    if (year < 100) year += 2000;
    let goalFYStart = (month >= fyStartMonth) ? year : year - 1;

    let currFYStart = (currMonth >= fyStartMonth) ? currYear : currYear - 1;

    if (goalFYStart === currFYStart) return "current";
    if (goalFYStart < currFYStart) return "past";
    return "future";
  }
}

export const getMinValue = (data, key, frequency, monthValues) => {
  const currKey = `${monthValues[key - 1]}${monthValues[key - 1] < monthValues[0] ? new Date().getFullYear() : new Date().getFullYear() - 1}`
  const min = _.minBy(data, (metric) => {
    if (frequency === "Daily") {
      if (metric.periodKey && metric.periodKey === currKey && metric.periodStatus !== "NON_WORKING") {
        return metric.value
      }
    } else if (frequency === "Weekly") {
      const monthWeeks = getISOWeeksInMonth(new Date(new Date().getFullYear(), monthValues[key - 1], 0))
      if (metric.periodKey && metric.periodKey === `2020` && monthWeeks.includes(metric.periodValue) && metric.periodStatus !== "NON_WORKING") {
        return metric.value
      }
    }
  });
  return min ? min.value : 0;
}

export const getMaxValue = (data, key, frequency, monthValues) => {
  const currKey = `${monthValues[key - 1]}${monthValues[key - 1] < monthValues[0] ? new Date().getFullYear() : new Date().getFullYear() - 1}`
  const max = _.maxBy(data, (metric) => {
    if (frequency === "Daily") {
      if (metric.periodKey && metric.periodKey === currKey && metric.periodStatus !== "NON_WORKING") {
        return metric.value
      }
    } else if (frequency === "Weekly") {
      const monthWeeks = getISOWeeksInMonth(new Date(new Date().getFullYear(), monthValues[key - 1], 0))
      if (metric.periodKey && metric.periodKey === `2020` && monthWeeks.includes(metric.periodValue) && metric.periodStatus !== "NON_WORKING") {
        return metric.value
      }
    }
  });
  return max ? max.value : 0;
}

export const getTotal = (data, key, frequency, monthValues) => {
  const currKey = `${monthValues[key - 1]}${monthValues[key - 1] < monthValues[0] ? new Date().getFullYear() : new Date().getFullYear() - 1}`
  const total = _.sumBy(data, (metric) => {
    if (frequency === "Daily") {
      if (metric.periodKey && metric.periodKey === currKey && metric.periodStatus !== "NON_WORKING") {
        return metric.value || 0
      }
    } else if (frequency === "Weekly") {
      const monthWeeks = getISOWeeksInMonth(new Date(new Date().getFullYear(), monthValues[key - 1], 0))
      if (metric.periodKey && metric.periodKey === `2020` && monthWeeks.includes(metric.periodValue) && metric.periodStatus !== "NON_WORKING") {
        return metric.value || 0
      }
    }
  });
  return !isNaN(parseFloat(total)) ? parseFloat(total).toFixed(2) : 0;
}

export const countTriggers = (data, key, frequency, monthValues) => {
  let triggerDays = 0;
  const currYear = `${monthValues[key - 1] < monthValues[0] ? new Date().getFullYear() : new Date().getFullYear() - 1}`
  _.each(data, (metric) => {
    if (frequency === "Daily") {
      if (metric.periodKey && metric.periodKey === `${monthValues[key - 1]}${currYear}`) {
        if (metric.periodStatus === "TRIGGER") triggerDays++
      }
    } else if (frequency === "Weekly") {
      const monthWeeks = getISOWeeksInMonth(new Date(new Date().getFullYear(), monthValues[key - 1], 0))
      if (metric.periodKey && metric.periodKey === currYear && monthWeeks.includes(metric.periodValue)) {
        if (metric.periodStatus === "TRIGGER") triggerDays++
      }
    }
  });

  return triggerDays;
}

export const countRedDays = (data, key, frequency, monthValues) => {
  let redDays = 0;
  const currYear = `${monthValues[key - 1] < monthValues[0] ? new Date().getFullYear() : new Date().getFullYear() - 1}`
  _.each(data, (metric) => {
    if (metric.value) {
      if (frequency === "Daily") {
        if (metric.periodKey && metric.periodKey === `${monthValues[key - 1]}${currYear}` && metric.periodStatus !== "NON_WORKING") {
          if (metric.status === "RED") redDays++
        }
      } else if (frequency === "Weekly") {
        const monthWeeks = getISOWeeksInMonth(new Date(new Date().getFullYear(), monthValues[key - 1], 0))
        if (metric.periodKey && metric.periodKey === currYear && monthWeeks.includes(metric.periodValue) && metric.periodStatus !== "NON_WORKING") {
          if (metric.status === "RED") redDays++
        }
      }
    }
  });

  return redDays;
}

export const getAverage = (data, key, frequency, monthValues) => {
  let total = 0
  let count = 0
  const currYear = `${monthValues[key - 1] < monthValues[0] ? new Date().getFullYear() : new Date().getFullYear() - 1}`
  _.each(data, (metric) => {
    if (metric.value && metric.periodStatus !== "NON_WORKING") {
      if (frequency === "Daily") {
        if (metric.periodKey && metric.periodKey === `${monthValues[key - 1]}${currYear}`) {
          total += metric.value
          count++
        }
      } else if (frequency === "Weekly") {
        const monthWeeks = getISOWeeksInMonth(new Date(new Date().getFullYear(), monthValues[key - 1], 0))
        if (metric.periodKey && metric.periodKey === currYear && monthWeeks.includes(metric.periodValue) && metric.periodStatus !== "NON_WORKING") {
          total += metric.value
          count++
        }
      }
    }
  });
  const average = count > 0 ? total / count : 0
  return parseFloat(average ? average : 0).toFixed(2);
}

export const getDaysInMonthArray = (date) => {
  let days = [];
  var x = getDaysInMonth(date);

  for (let index = 0; index < x; index++) {
    days.push(index + 1);
  }

  return days;
}

export const getPeriodValue = (value, months) => {
  return months[value - 1]
}

export const getWeeklyPeriodValue = (value, date) => {
  console.log(date)
  return value
}

export const getCalculatedCurrentTarget = (targets, date, monthValues, dueDate, period, periodValue) => {
  const month = getMonth(Date.parse(dueDate));
  const monthIndex = monthValues.indexOf(month + 1)

  const currentMonth = period === "MONTH" ? periodValue : getMonth(date)
  const currIndex = monthValues.indexOf(currentMonth)

  let climb;
  if (targets[0] > targets[1]) {
    climb = (targets[0] - targets[1]) / (monthIndex)
  } else if (targets[0] < targets[1]) {
    climb = (targets[1] - targets[0]) / (monthIndex)
  } else {
    climb = 0
  }

  if (currIndex < monthIndex) {
    if (targets[0] > targets[1]) {
      return parseFloat(targets[0] - (climb * currIndex)).toFixed(2)
    } else {
      return parseFloat(targets[0] + (climb * currIndex)).toFixed(2)
    }
  } else {
    return targets[1]
  }
}

export const getPeriod = (frequency) => {
  let period;

  switch (frequency) {
    case "Daily":
      period = "DAY"
      break;
    case "Weekly":
      period = "WEEK"
      break;
    default:
      period = "MONTH"
  }

  return period
}

export const getDisabledDays = metrics => {
  const disabledDays = Object.entries(metrics).reduce(
    (all, [period, { status, periodValue }]) => {

      if (status === "NON_WORKING") all.add(parseInt(periodValue));
      return all;
    },
    new Set()
  );

  return disabledDays
}

export const getCalculatedDisplayWeek = (week, startWeek) => {
  if (!startWeek) return week

  if (week < startWeek) {
    return 53 - (startWeek - week) + 1
  } else {
    return week - startWeek + 1
  }
}

export const formatDate = (date, frequency, monthList, monthValues, period, startWeek) => {
  switch (frequency) {
    case "Daily":
      return period + " " + MONTHS[date.getMonth()] + " " + date.getFullYear()
    case "Weekly":
      return "Wk " + getCalculatedDisplayWeek(getWeeklyPeriodValue(period, date), startWeek ? startWeek.week : null) + " " + date.getFullYear()
    default:
      return MONTHS[period - 1] + " " + (period >= monthValues[0] ? (date.getMonth() + 1 >= monthValues[0] ? `${date.getFullYear()}` : `${date.getFullYear() - 1}`) : (date.getMonth() + 1 >= monthValues[0] ? `${date.getFullYear() + 1}` : `${date.getFullYear()}`))
  }
}
